import { IProductDTO } from '@legacy/shared-types/dto/product.type';
import { Alert, App, Flex, Spin } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { btlProductApi } from '@api/product/btlProductApi';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { isOoProductEditableStatus } from '@components/CaseSidebar/config/ooStatus';
import { FormValidationContext, useOnNext } from '@hooks/useBtlFormValidation';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useBtlProductByAppId } from '@queries/products/useBtlProductByAppId';
import { TypedFormatMessageFn } from '@app-types/intl';
import type { OoFormRouteContext } from '../types';
import { useOoFormMappedRoutes } from '../useOoFormMappedRoutes';
import { OoProductItem } from './OoProductItem';

interface OoProductSelectorProps {
  routeContext: OoFormRouteContext;
}

export function OoProductSelector({ routeContext }: OoProductSelectorProps) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<IProductDTO>();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const caseId = routeContext.caseId;
  const { data, isLoading } = useBtlApplicationInProgressById(caseId);
  const isProductEditable = isOoProductEditableStatus(data?.status);
  const { data: products, isLoading: isProductsLoading } = useBtlProductByAppId(caseId);
  const { nextSection } = useOoFormMappedRoutes(routeContext);

  const handleNavigate = (itemId: CaseSidebarItemId) => {
    navigate({ search: `?section=${itemId}` });
  };

  const formValidationContext = useContext(FormValidationContext);
  if (!formValidationContext) {
    throw new Error('OoProductSelector must be used within a FormValidationProvider');
  }
  const { setIsValid, setIsSubmitting } = formValidationContext;

  useEffect(() => {
    setIsValid(!isProductEditable || Boolean(selectedProduct));
  }, [isProductEditable, selectedProduct, setIsValid]);

  useEffect(() => {
    setSelectedProduct(data?.product);
  }, [data]);

  useOnNext(async () => {
    if (!isProductEditable || !selectedProduct) {
      return;
    }

    setIsSubmitting(true);
    try {
      await btlProductApi.updateBtlProduct(caseId, selectedProduct.id);
      if (nextSection) {
        handleNavigate(nextSection);
      }
    } catch (error: unknown) {
      console.error(error);
      message.error(error instanceof Error ? error.message : 'Failed to update product');
    } finally {
      setIsSubmitting(false);
    }
  });

  if (isLoading || isProductsLoading) {
    return <Spin size={'large'} />;
  }

  const caseProduct = data?.product;
  const activeProducts = products?.filter(product => product.isActive) ?? [];
  // A product can be deactivated after a case has already been placed on it. The case still needs to
  // show what it is on, so keep the case's own product in the list even when it is no longer active.
  const isCaseProductListed = activeProducts.some(product => product.id === caseProduct?.id);
  const availableProducts = caseProduct && !isCaseProductListed ? [...activeProducts, caseProduct] : activeProducts;
  const sortedProducts = availableProducts.sort(
    (productA, productB) =>
      productA.monthsInitial - productB.monthsInitial || productA.maximumFTV - productB.maximumFTV,
  );

  return (
    <Flex gap={16} wrap align='stretch'>
      {sortedProducts.length ? (
        sortedProducts.map(product => {
          const isInvalidated = !data?.isCurrentProductSuitable && product.id === caseProduct?.id;

          return (
            <OoProductItem
              key={product.id}
              product={product}
              isSelected={selectedProduct?.id === product.id}
              onProductSelected={nextProduct => setSelectedProduct(nextProduct)}
              isSelectable={isProductEditable}
              style={isInvalidated ? { opacity: 0.5 } : undefined}
              invalidated={isInvalidated}
            />
          );
        })
      ) : (
        <Alert
          description={formatMessage({ id: 'ooApplicationForm.PRODUCT.noProducts.message' })}
          message={formatMessage({ id: 'ooApplicationForm.PRODUCT.noProducts.title' })}
          type='warning'
          showIcon
        />
      )}
    </Flex>
  );
}
